// Third party
import React, { Component, ReactNode } from 'react';
import classNames from 'classnames';
import { RouteComponentProps, withRouter } from 'react-router';

// Components
import DataTableV2 from '../data/DataTableV2/DataTableV2';
import EmptySearchResults from '@/views/components/data/DataTableV2/EmptySearchResults';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import { LoadingSpinner } from '../common';
import { withErrorBoundary } from '../errors';

// Enums
import { SearchType } from '@/enums/AdvancedSearch';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Types
import { ColumnDef, Row, VisibilityState } from '@tanstack/react-table';
import { MultiselectAction } from '@/types/data-table';

// Utils
import { formatMessage } from '@/utils/i18n';
import AdvancedSearchItem, { AdvancedSearchItemForDataTable } from '@/types/advanced-search-item';

// Legacy Component Support
import LegacyAdvancedSearchResults from './LegacyAdvancedSearchResults';

interface Props<T> {
  actions?: MultiselectAction<AdvancedSearchItem>[];
  canSelect?: boolean;
  classicVersionOnly?: boolean;
  className?: string;
  columns: (ColumnDef<AdvancedSearchItem> & { includeInCompact: boolean })[];
  columnPinning?: { left?: string[]; right?: string[] };
  compact?: boolean;
  customTableActions?: MultiselectAction<AdvancedSearchItem>[];
  data?: AdvancedSearchItem[];
  showNoDataComponent?: boolean;
  focusedItem: AdvancedSearchItem;
  hasMore: boolean;
  insideModal?: boolean;
  itemDetails?: ReactNode;
  itemType: SearchType;
  loading?: boolean;
  onClearSelection: () => void;
  onLoadMoreResults: () => void;
  onRowClick: (item: T) => void;
  onRowSelectionChange: (selectedRows: T[]) => void;
  selectedTitle?: string;
  totalResultsCount?: number;
  useWindowScrollAncestor?: boolean;
  searchQuery?: string;
  selectedItems?: T[];

  savedItems?: AdvancedSearchItemForDataTable[];
  columnVisibilityState?: VisibilityState;
}

class AdvancedSearchResultV2 extends Component<Props<AdvancedSearchItem> & RouteComponentProps> {
  static defaultProps = {
    canSelect: true,
    showNoDataComponent: false,
  };

  componentDidMount() {
    const scrollElement = !this.props.insideModal ? window : document.getElementsByClassName('screener-modal')[0];

    if (!scrollElement) {
      return;
    }

    scrollElement.addEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(prevProps: Props<AdvancedSearchItem>) {
    if (this.props.focusedItem && !prevProps.focusedItem) {
      setTimeout(() => {
        this.positionItemDetails();
      }, 100);
    }
  }

  componentWillUnmount() {
    const scrollElement = !this.props.insideModal ? window : document.getElementsByClassName('screener-modal')[0];

    if (!scrollElement) {
      return;
    }

    scrollElement.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = () => {
    this.positionItemDetails();
  };

  positionItemDetails = () => {
    const advancedSearchContainer = document.getElementsByClassName('advanced-search')[0] as HTMLElement;
    const items = advancedSearchContainer?.getElementsByClassName('tst-tbr') as HTMLCollection;

    if (!items?.length) {
      return;
    }

    const table = advancedSearchContainer.getElementsByClassName('search-results-v2')[0] as HTMLElement;
    const itemDetails = advancedSearchContainer.getElementsByClassName('item-details') as HTMLCollection;
    const detailsContainer = advancedSearchContainer.getElementsByClassName('details-container') as HTMLCollection;
    const lastItem = items[items.length - 1] as HTMLElement;

    const noItemDetails = itemDetails.length === 0 || detailsContainer.length === 0;

    const popup = detailsContainer[0] as HTMLElement;
    const container = itemDetails[0] as HTMLElement;

    if (noItemDetails || lastItem.offsetTop + lastItem.offsetHeight < popup.offsetHeight) {
      return;
    }

    if (table.getBoundingClientRect().top < 0) {
      // Popup is already positioned properly.
      if (popup.style.position === 'fixed') {
        return;
      }

      const width = `${popup.getBoundingClientRect().width}px`;

      popup.style.position = 'fixed';
      container.style.width = width;
      container.style.minWidth = width;
      container.style.maxWidth = width;
      popup.style.width = width;
    } else {
      popup.style.removeProperty('position');
      container.style.removeProperty('width');
      container.style.removeProperty('minWidth');
      popup.style.removeProperty('width');
    }
  };

  getActions = () => {
    const { customTableActions } = this.props;

    if (customTableActions !== undefined) {
      return customTableActions;
    }

    return !this.props.insideModal ? this.props.actions! : [];
  };

  formatData = () => {
    const { focusedItem, savedItems } = this.props;

    if (!this.props.data) {
      return [];
    }

    return this.props.data.map(i => ({
      ...i,
      isFocused: focusedItem && focusedItem._id === i._id,
      isSaved: savedItems && savedItems.some(s => s._id === i._id),
    })) as AdvancedSearchItem[];
  };

  computeFocusedClassName = (row: Row<AdvancedSearchItem>) => {
    const { focusedItem } = this.props;
    return focusedItem && focusedItem._id === row.original._id ? 'focused' : '';
  };

  renderEmptyState = () => {
    const { data, loading, searchQuery, showNoDataComponent } = this.props;
    const hasNoData = !!data && data.length === 0;

    if (!hasNoData && !loading) {
      return null;
    }

    if (!searchQuery && showNoDataComponent) {
      return this.renderNoDataComponent();
    }

    return this.renderNoResultsComponent();
  };

  renderNoDataComponent = () => {
    const { itemType } = this.props;
    const i18nEmptyStatePath = `Search.Saved${itemType}.NoData`;
    const linkSearchPage = { label: `${i18nEmptyStatePath}.GetStarted`, onClick: () => this.props.history.replace(`/search/${itemType.toLowerCase()}`) };
    return (
      <div className="no-data-component">
        <EmptyStateCard messagePath={`${i18nEmptyStatePath}.Text`} links={[linkSearchPage]} titlePath={`${i18nEmptyStatePath}.Title`} />
      </div>
    );
  };

  renderNoResultsComponent = () => {
    const { searchQuery } = this.props;
    return (
      <div className="no-data-component">
        <EmptySearchResults
          searchQuery={searchQuery}
          titleToLocalize="AdvancedSearch.NoResults.NoResultsFound"
          messageToLocalize="AdvancedSearch.NoResults.NotMatch"
        />
      </div>
    );
  };

  getRowAriaLabelledBy = (row: { original: AdvancedSearchItem }) => `fund-name-${row.original._id}`;

  render() {
    const {
      className,
      columns,
      columnVisibilityState,
      loading,
      data,
      totalResultsCount,
      hasMore,
      focusedItem,
      itemDetails,
      selectedItems,
      selectedTitle,
      canSelect,
    } = this.props;
    const noDataToDisplay = loading || !this.props.data || this.props.data.length === 0;
    const hasSearchQuery = !loading && !!this.props.data && this.props.data.length === 0;
    const tableIsHidden = noDataToDisplay && !hasSearchQuery;
    const classes = classNames('search-results-v2', className);

    return (
      <div className={classes} data-testid="advanced-search-result" aria-live="polite">
        {loading && <LoadingSpinner message={formatMessage('Common.LoadingResults')} />}
        {!loading && (
          <span className="visually-hidden">
            {totalResultsCount} {formatMessage('Common.Accessibility.SearchResults')}
          </span>
        )}

        {!tableIsHidden && (
          <div className="results-table">
            <DataTableV2<AdvancedSearchItem>
              columns={columns}
              columnPinningState={this.props.columnPinning}
              columnVisibilityState={columnVisibilityState}
              data={this.formatData()}
              onClearSelection={this.props.onClearSelection}
              noDataComponent={this.renderEmptyState}
              onRowClick={this.props.onRowClick}
              onRowSelectionChange={this.props.onRowSelectionChange}
              enableMultiselect
              enableTableSelection={canSelect}
              enableSorting
              enableColumnFreeze={!!this.props.columnPinning}
              getAdditionalRowClassNames={this.computeFocusedClassName}
              getRowAriaLabelledBy={this.getRowAriaLabelledBy}
              infiniteScrollOptions={{
                onEnter: this.props.onLoadMoreResults,
                show: !!(data && totalResultsCount != null && data.length < totalResultsCount && hasMore),
                scrollAncestor: this.props.insideModal && this.props.useWindowScrollAncestor ? window : undefined,
                waypointKey: data?.length || 0,
              }}
              multiselectActionsOptions={{
                actions: this.getActions(),
                selectedTitle: selectedTitle ?? '',
                labelAccessor: 'names.full',
                overrideSelectedData: selectedItems,
              }}
              perfectScrollBarOptions={{
                suppressScrollX: false,
                suppressScrollY: false,
              }}
              tableTestId="fund-search-results-table"
            />
          </div>
        )}

        {!!focusedItem && itemDetails}
      </div>
    );
  }
}

const AdvancedSearchResult = (props: Props<AdvancedSearchItem> & RouteComponentProps) => {
  const { isLightVersion } = useThemeVersion();
  const { classicVersionOnly } = props;
  return isLightVersion && !classicVersionOnly ? <AdvancedSearchResultV2 {...props} /> : <LegacyAdvancedSearchResults {...props} />;
};

export default withRouter(withErrorBoundary(AdvancedSearchResult));
